<script setup lang="ts">
import { readMateAdoptionExplorePage } from "@/apis/mate";
import { readWorldviewExplorePage } from "@/apis/user";
import { BottomSheetLayoutInstance } from "@/components/layout/BottomSheetLayout.vue";
import PlanetOverview from "@/components/mobile/common/PlanetOverview.vue";
import SeasonRankHome from "@/components/mobile/rank/SeasonRankHome.vue";
import { ThreadListInstance } from "@/components/mobile/ThreadList.vue";
import BoxedTab from "@/components/mobile/common/BoxedTab.vue";
import { isMobile } from "@/modules/common";
import { useGlobalUser } from "@/stores/globalUser";
import { useInteractive } from "@/stores/interactive";
import { useMate } from "@/stores/mate";
import { useMoment } from "@/stores/moment";
import { MateAdoption, Moment, Worldview } from "@/types/entity";
import { nextTick, onActivated, onMounted, ref, UnwrapRef, watch } from "vue";
import { RouteLocationRaw, useRouter } from "vue-router";
import { MateType } from "@/enums/mateEnums";

const user = useGlobalUser();
const mateStore = useMate();
const interactiveStore = useInteractive();
const momentStore = useMoment();

const followingMomentListRef = ref<ThreadListInstance | null>(null);
const starPlanetListRef = ref<ThreadListInstance | null>(null);
const rankListRef = ref<ThreadListInstance | null>(null);
const bottomSheetLayoutRef = ref<BottomSheetLayoutInstance | null>(null);
const active = ref("moment");

const hideTop = ref(false);
const onScrollToHideTop = (h: number, originHeight: number) => {
  if (h === 0) return (hideTop.value = false);
  if (Math.abs(h - originHeight) < 20) return;
  if (h - originHeight > 0) {
    hideTop.value = true;
  } else {
    hideTop.value = false;
  }
};
const showActions = ref(false);
const toOperateMoment = ref<Moment | null>(null);
const onActionClick = (moment: UnwrapRef<Moment>) => {
  toOperateMoment.value = moment;
  showActions.value = true;
};

// const timer = ref<NodeJS.Timeout | undefined>();
watch(
  () => user.isLogin,
  (val) => {
    if (!val) return;
    interactiveStore.getPoolList();
  }
);

watch(
  active,
  () => {
    bottomSheetLayoutRef.value?.refreshScrollContainer();
  },
  { flush: "post" }
);

const initMomentScrollTop = () => {
  nextTick(() => {
    // newMomentListRef.value?.scrollTo(momentStore.data[0].scrollTop);
    followingMomentListRef.value?.scrollTo(momentStore.data[2].scrollTop);
  });
};

const initAdoptionScrollTop = () => {
  nextTick(() => {
    scroller.value?.scrollTo(height.value);
  });
};

const getScrollContainer = () => {
  if (active.value === "moment") {
    return document.querySelector(".followingMoment") as HTMLElement;
  } else if (active.value === "interactionLog") {
    return document.querySelector(".interaction-event-list") as HTMLElement;
  } else if (active.value === "starPlanet") {
    return document.querySelector(".star-planet-list") as HTMLElement;
  } else if (active.value === "rank") {
    return document.querySelector(".rank-list") as HTMLElement;
  } else {
    return document.querySelector(".rank-list") as HTMLElement;
  }
};

const starWorldviewList = ref<Worldview[]>([]);
const initWorldviewList = async () => {
  const res = await readWorldviewExplorePage();
  starWorldviewList.value = res.page.data;
};
const loadingWorldviewList = ref(false);

const showOcSelector = ref(false);
const onConfirmOc = (value?: number) => {
  mateStore.changeCurrentMate(value!);
  showOcSelector.value = false;
};

onMounted(() => {
  initMomentScrollTop();
  initWorldviewList();
});

onActivated(() => {
  if (!user.isLogin) return;
  initMomentScrollTop();
  initAdoptionScrollTop();
});

const userStore = useGlobalUser();
const list = ref<MateAdoption[]>([]);
const scroller = ref<ThreadListInstance>();
const router = useRouter();

const adoptionActiveTab = ref("all");
const adoptionActiveTabIdx = ref(0);

const ADOPTION_TYPE_OPTIONS = [
  { value: "all", label: "全部" },
  { value: "original", label: "原创" },
  { value: "fanswork", label: "二创" },
];
const reload = async () => {
  list.value = [];
  if (userStore.isLogin) mateStore.fetchMateList();
  scroller.value?.resetScrollLoadingState();
};

const reloadAdoption = async () => {
  list.value = [];
  scroller.value?.resetScrollLoadingState();
};

const handleAdoptionTypeChange = async (idx: number) => {
  adoptionActiveTabIdx.value = idx;
  adoptionActiveTab.value = ADOPTION_TYPE_OPTIONS[idx].value;
  await reloadAdoption();
};

const loadNextPage = async () => {
  const { length } = list.value;
  if (length !== 0 && length % 10 !== 0) return false;

  const page = length / 10 + 1;
  // 构建筛选参数
  const filter: { status: 1; mateType?: MateType } = { status: 1 };
  const selectedType = ADOPTION_TYPE_OPTIONS[adoptionActiveTabIdx.value].value;

  if (selectedType === "original") {
    filter.mateType = MateType.Original; // MateType.Original
  } else if (selectedType === "fanswork") {
    filter.mateType = MateType.Fanswork; // MateType.Fanswork
  }

  const res = await readMateAdoptionExplorePage({
    filter,
    pagination: { page, perPage: 10 },
  });

  if (selectedType !== ADOPTION_TYPE_OPTIONS[adoptionActiveTabIdx.value].value)
    return true;

  list.value = [...list.value, ...res!.page.data];

  return res!.page.data.length === 10;
};
const height = ref(0);
const onMateItemClicked = async (to: RouteLocationRaw) => {
  if (!userStore.isLogin) return userStore.openLoginModal("register");

  router.push(to);
};
</script>
<template>
  <div class="w-full h-full grid grid-rows-[auto_auto_1fr]">
    <ThreadsNav :show-center="false">
      <template #right v-if="user.isLogin">
        <!-- <CrystalPowerCount /> -->
        <AddQQGroupBtn />
      </template>
    </ThreadsNav>
    <VanTabs
      v-model:active="active"
      style="--van-tabs-nav-background: transparent"
      class="pb-1 bg-base-200 mb-2"
    >
      <VanTab :title="$t('Community.moment')" name="moment" />
      <VanTab :title="$t(`adoption.adopt`)" name="adopt" />
      <VanTab :title="$t('Community.rank')" name="rank" />
      <!-- <VanTab :title="$t(`Community.starPlanet`)" name="starPlanet" /> -->
    </VanTabs>
    <div class="w-full h-full overflow-hidden">
      <div
        v-if="active === 'moment'"
        class="h-full overflow-hidden flex flex-col"
      >
        <div
          v-if="user.isLogin"
          class="flex items-center justify-between pl-2 pr-3 py-2"
        >
          <div class="flex items-center gap-1">
            <div class="text-xs">{{ $t("Home.currentIdentity") }}：</div>
            <div class="btn btn-xs btn-outline" @click="showOcSelector = true">
              <div class="max-w-20 flex items-center gap-1">
                <span class="flex-1 line-clamp-1 whitespace-nowrap">
                  {{ mateStore.currentMate?.name }}
                </span>
                <i class="iconify mingcute--triangle-fill rotate-180 w-2.5"></i>
              </div>
            </div>

            <ModalSelectOption
              v-model:show="showOcSelector"
              :options="[
                ...mateStore.fullList
                  .filter((mate) => mate.name)
                  .map((mate) => ({
                    text: mate.name,
                    value: mate.id,
                  })),
              ]"
              :initial-value="mateStore.currentMate?.id"
              @confirm="onConfirmOc"
            />
          </div>

          <div class="flex items-center gap-2">
            <button
              class="btn btn-xs rounded"
              @click="onMateItemClicked({ name: 'threadList' })"
            >
              {{ $t(`Buttons.myThread`) }}
            </button>
            <button
              class="btn btn-xs rounded btn-primary"
              @click="onMateItemClicked({ name: 'createThread' })"
            >
              {{ $t("Buttons.createThread") }}
            </button>
          </div>
        </div>
        <div class="flex-1 overflow-auto">
          <thread-list
            ref="followingMomentListRef"
            :on-pull-load="() => momentStore.initMomentListIfEmpty(true)"
            :on-scroll-load="() => momentStore.fetchMomentList(true)"
            class="px-2 followingMoment"
            :need-login="false"
            enable-pull-load
            @on-scroll="
              (h) => (
                onScrollToHideTop(h, momentStore.data[2].scrollTop),
                (momentStore.data[2].scrollTop = h)
              )
            "
          >
            <thread-item1
              v-for="moment in momentStore.exploreMomentList"
              :key="moment.uuid"
              :moment="moment"
              enable-audio-play
              enable-show-more-link
              showCoinVote
              showReplyList
              :showCreateTime="false"
              @reaction-click="onActionClick"
            />
          </thread-list>
        </div>
      </div>
      <thread-list
        v-if="active === 'starPlanet'"
        ref="starPlanetListRef"
        :on-pull-load="initWorldviewList"
        enable-pull-load
        :enable-scroll-load="false"
        :need-login="false"
        class="star-planet-list"
      >
        <div v-if="loadingWorldviewList">
          <div class="flex justify-center items-center h-full">
            <i class="loading loading-ring w-6 mr-2" />
          </div>
        </div>
        <div v-else-if="starWorldviewList.length === 0">
          <VanEmpty :description="$t(`Message.underConstruction`)" />
        </div>
        <PlanetOverview
          class="w-full h-full"
          v-else
          :worldview-list="starWorldviewList"
          mode="explore"
          :disable-x-scroll="isMobile()"
          @view-more="bottomSheetLayoutRef?.pullUpPanel()"
        />
      </thread-list>
      <ThreadList
        v-if="active === 'adopt'"
        ref="scroller"
        :on-pull-load="reload"
        :on-scroll-load="loadNextPage"
        @on-scroll="(h) => (height = h)"
        class="w-full"
      >
        <div>
          <div
            class="red-dot flex items-center mb-2 mt-2 ml-5 justify-between mr-2"
          >
            <div class="mr-2 font-semibold flex items-center gap-1">
              {{ $t(`adoption.adopt`) }}

              <BoxedTab
                v-model:selected-idx="adoptionActiveTabIdx"
                class="ml-2"
                @update:selected-idx="handleAdoptionTypeChange"
              >
                <div v-for="item in ADOPTION_TYPE_OPTIONS" :key="item.value">
                  {{ item.label }}
                </div>
              </BoxedTab>
            </div>

            <div class="flex gap-1">
              <button
                class="btn btn-xs rounded"
                @click="
                  onMateItemClicked({
                    name: 'adoptionHome',
                    query: {
                      type: 'myAdoption',
                    },
                  })
                "
              >
                {{ $t(`adoption.myAdoption`) }}
              </button>
              <button
                class="btn btn-xs rounded btn-primary"
                @click="
                  onMateItemClicked({
                    name: 'adoptionCreate',
                  })
                "
              >
                {{ $t("adoption.publishAdoption") }}
              </button>
            </div>
          </div>
          <MateAdoptionWaterfall :list="list" activeTab="adoptable" />
        </div>
      </ThreadList>
      <ThreadList
        v-if="active === 'rank'"
        ref="rankListRef"
        enable-pull-load
        :enable-scroll-load="false"
        :need-login="false"
        class="rank-list"
      >
        <SeasonRankHome class="h-full" enable-pull-load />
      </ThreadList>
    </div>
  </div>
</template>
<style>
#home-page .van-tabs {
  --van-tabs-line-height: 36px;
}
</style>
